import React, { useEffect, useMemo, useState } from 'react';
import { Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import { AppModal } from '../AppModal';
import { formatDisplayName } from '../../utils/display-name';
import { formatInviteReason } from '../../utils/invite-error';

function displayName(name, email) {
  const n = formatDisplayName(name);
  if (n) return n;
  return (email || '').trim() || '—';
}

function RecipientStatusMark({ status, reason }) {
  if (!status) return null;
  if (status === 'pending') {
    return (
      <span
        className="invite-send-pend"
        role="status"
        aria-label="Sending"
        data-testid="invite-recipient-pending"
      />
    );
  }
  if (status === 'failed') {
    return (
      <span
        className="invite-send-mark invite-send-mark-failed"
        aria-label={reason || 'Failed'}
        title={reason || 'Failed'}
        data-testid="invite-recipient-failed"
      >
        ×
      </span>
    );
  }
  if (status === 'skipped') {
    return (
      <span
        className="invite-send-mark invite-send-mark-skipped"
        aria-label={reason || 'Skipped'}
        title={reason || 'Skipped'}
        data-testid="invite-recipient-skipped"
      >
        –
      </span>
    );
  }
  // sent | already_sent
  return (
    <span
      className="invite-send-mark invite-send-mark-sent"
      aria-label={status === 'already_sent' ? 'Already sent' : 'Sent'}
      data-testid="invite-recipient-sent"
    >
      ✓
    </span>
  );
}

RecipientStatusMark.propTypes = {
  status: PropTypes.string,
};

export function InviteEmailModal({
  open,
  onClose,
  onConfirm,
  recipients = [],
  recipientStatuses = {},
  recipientReasons = {},
  waitingUntil = null,
  subject: subjectProp = '',
  bodyHtml = '',
  templateLabel = '',
  templateLoading = false,
  loading = false,
  error = null,
}) {
  const [subject, setSubject] = useState(subjectProp);

  useEffect(() => {
    if (open) {
      setSubject(subjectProp);
    }
  }, [open, subjectProp]);

  const countLabel = recipients.length === 1 ? '1 member' : `${recipients.length} members`;
  const sendLabel =
    recipients.length === 1 ? 'Send to 1 member' : `Send to ${recipients.length} members`;
  const singleClass = recipients.length === 1 ? ' invite-email-recipients-single' : '';
  const busy = loading || templateLoading;

  const recipientRows = useMemo(
    () =>
      recipients.map((r) => ({
        key: r.id || r.email,
        id: r.id,
        name: displayName(r.name, r.email),
        email: (r.email || '').trim() || '—',
        status: recipientStatuses[r.id] || null,
        reason: formatInviteReason(
          recipientReasons[r.id]?.reason,
          recipientReasons[r.id]?.detail
        ),
      })),
    [recipients, recipientStatuses, recipientReasons]
  );

  const waitingSeconds = (() => {
    if (!waitingUntil) return 0;
    const until = Number(waitingUntil);
    if (!Number.isFinite(until)) return 0;
    const ms = until > 1e12 ? until : until * 1000;
    return Math.max(0, Math.ceil((ms - Date.now()) / 1000));
  })();

  const allSettled =
    recipientRows.length > 0
    && recipientRows.every((r) => r.status && r.status !== 'pending');

  const previewHtml = templateLoading
    ? '<p>Loading Front template…</p>'
    : bodyHtml || '<p>No template body returned from Front.</p>';

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={null}
      showCloseButton={false}
      actions={null}
      maxWidth="md"
      fullWidth
      dividers={false}
      className="invite-email-app-modal"
    >
      <div className="invite-email-modal invite-email-modal-bulk">
        <IconButton
          aria-label="Close"
          onClick={onClose}
          className="invite-email-close"
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <div className={`invite-email-recipients${singleClass}`}>
          <div className="invite-email-header">
            <span className="invite-email-label">Recipients</span>
            <span className="invite-email-label invite-email-count">{countLabel}</span>
          </div>
          <ul className="invite-recipient-list">
            {recipientRows.map((r) => (
              <li key={r.key} className="invite-recipient-item">
                <span className="invite-recipient-name">
                  <RecipientStatusMark status={r.status} reason={r.reason} />
                  {r.name}
                </span>
                <span className="invite-recipient-email">{r.email}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="invite-email-subject">
          <div className="invite-email-header">
            <label className="invite-email-label" htmlFor="invite-email-subject">
              Subject
            </label>
          </div>
          <input
            id="invite-email-subject"
            type="text"
            className="invite-email-subject-input"
            value={templateLoading ? 'Loading…' : subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={busy}
            readOnly
          />
        </div>

        <div className="invite-email-body">
          <div className="invite-email-header">
            <span className="invite-email-label" id="invite-email-body-label">
              Body
            </span>
            <span className="invite-email-label invite-email-template-id">
              {templateLoading ? 'Loading…' : templateLabel}
            </span>
          </div>
          <div
            className="invite-email-preview"
            role="textbox"
            aria-multiline="true"
            aria-labelledby="invite-email-body-label"
            // Preview only — outbound content is sent via Front templates.
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
          <div className="invite-email-actions">
            {waitingSeconds > 0 ? (
              <p className="invite-email-waiting" role="status" data-testid="invite-waiting">
                Waiting for Front rate limit, resumes in {waitingSeconds}s
              </p>
            ) : null}
            {error ? (
              <p className="invite-email-error" role="alert">
                {error}
              </p>
            ) : null}
            <Button
              variant="onyxPrimary"
              onClick={allSettled ? onClose : onConfirm}
              disabled={
                allSettled
                  ? false
                  : busy || recipients.length === 0 || Boolean(error && !bodyHtml)
              }
              className="invite-email-send"
            >
              {loading ? 'Sending…' : allSettled ? 'Done' : sendLabel}
            </Button>
          </div>
        </div>
      </div>
    </AppModal>
  );
}

InviteEmailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  recipients: PropTypes.arrayOf(PropTypes.object),
  recipientStatuses: PropTypes.objectOf(PropTypes.string),
  recipientReasons: PropTypes.objectOf(PropTypes.object),
  waitingUntil: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  subject: PropTypes.string,
  bodyHtml: PropTypes.string,
  templateLabel: PropTypes.string,
  templateLoading: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.string,
};
